import { createAsyncThunk, createSlice, AnyAction } from "@reduxjs/toolkit";
import CreateHabit from "./interfaces/CreateHabit";
import Habit from "./interfaces/Habit";
import IUpdateHabit from "./interfaces/IUpdateHabit";

interface HabitState {
  habits: Habit[];
  isHabitError: boolean;
  isHabitsSuccess: boolean;
  isHabitFetching: boolean;
  habitError: string[];
}

const initialState: HabitState = {
  habits: [],
  isHabitError: false,
  isHabitsSuccess: false,
  isHabitFetching: false,
  habitError: [],
};

export const getAllMyHabits = createAsyncThunk(
  "habit/getAllMyHabits",
  async (_, thunkAPI) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/habit/me`, {
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });

      if (response.status === 200) {
        const data = await response.json();

        return data;
      }
      const { statusCode, message } = await response.json();
      return thunkAPI.rejectWithValue({ statusCode, message });
    } catch (error) {
      return thunkAPI.rejectWithValue({
        statusCode: 500,
        message: "Internal Server Error.",
      });
    }
  }
);

export const createHabit = createAsyncThunk(
  "habit/createHabit",
  async ({ habit_name, habit_description }: CreateHabit, thunkAPI) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/habit/new`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          habit_name,
          habit_description,
        }),
      });

      if (response.status === 201) {
        const data = await response.json();

        return data;
      }
      const { statusCode, message } = await response.json();
      return thunkAPI.rejectWithValue({ statusCode, message });
    } catch (error) {
      return thunkAPI.rejectWithValue({
        statusCode: 500,
        message: "Internal Server Error.",
      });
    }
  }
);

export const updateHabit = createAsyncThunk(
  "habit/updateHabit",
  async (
    { habit_id, habit_name, habit_description }: IUpdateHabit,
    thunkAPI
  ) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/habit/${habit_id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            habit_name,
            habit_description,
          }),
        }
      );

      if (response.status === 200) {
        const data = await response.json();
        return data;
      }
      const { statusCode, message } = await response.json();
      return thunkAPI.rejectWithValue({ statusCode, message });
    } catch (error) {
      return thunkAPI.rejectWithValue({
        statusCode: 500,
        message: "Internal Server Error.",
      });
    }
  }
);

export const deleteHabit = createAsyncThunk(
  "habit/deleteHabit",
  async ({ habit_id }: IUpdateHabit, thunkAPI) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/habit/${habit_id}`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        }
      );

      if (response.status === 200) {
        const data = await response.json();
        return data;
      }
      const { statusCode, message } = await response.json();
      return thunkAPI.rejectWithValue({ statusCode, message });
    } catch (error) {
      return thunkAPI.rejectWithValue({
        statusCode: 500,
        message: "Internal Server Error.",
      });
    }
  }
);

const habitSlice = createSlice({
  name: "habit",
  initialState,
  reducers: {
    clearHabitState: (state) => {
      state.habits = [];
      state.isHabitError = false;
      state.habitError = [];
      state.isHabitsSuccess = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAllMyHabits.fulfilled, (state, { payload }) => {
      //   state.

      payload.map((p: Habit) => {
        state.habits.push(p);
      });
      //   state.isHabitError = false;
      state.isHabitsSuccess = true;
      state.isHabitFetching = false;
      return state;
    });
    builder.addCase(getAllMyHabits.pending, (state) => {
      state.isHabitFetching = true;
      return state;
    });
    builder.addCase(getAllMyHabits.rejected, (state, action: AnyAction) => {
      state.isHabitFetching = false;
      state.isHabitError = true;
      state.habitError.push(action?.payload?.message);
    });

    builder.addCase(createHabit.fulfilled, (state, { payload }) => {
      state.isHabitsSuccess = true;
      state.isHabitFetching = false;
      state.isHabitError = false;
      return state;
    });
    builder.addCase(createHabit.pending, (state) => {
      state.isHabitFetching = true;
      state.isHabitsSuccess = false;
      state.isHabitError = false;
      return state;
    });
    builder.addCase(createHabit.rejected, (state, action: AnyAction) => {
      state.isHabitFetching = false;
      state.isHabitError = true;
      state.habitError.push(action?.payload?.message);
      return state;
    });

    builder.addCase(updateHabit.fulfilled, (state, { payload }) => {
      state.isHabitsSuccess = true;
      state.isHabitFetching = false;
      state.isHabitError = false;
      return state;
    });
    builder.addCase(updateHabit.pending, (state) => {
      state.isHabitFetching = true;
      state.isHabitsSuccess = false;
      state.isHabitError = false;
      return state;
    });
    builder.addCase(updateHabit.rejected, (state, action: AnyAction) => {
      state.isHabitFetching = false;
      state.isHabitError = true;
      state.habitError.push(action?.payload?.message);
      return state;
    });

    builder.addCase(deleteHabit.fulfilled, (state, { payload }) => {
      state.isHabitsSuccess = true;
      state.isHabitFetching = false;
      state.isHabitError = false;
      return state;
    });
    builder.addCase(deleteHabit.pending, (state) => {
      state.isHabitFetching = true;
      state.isHabitsSuccess = false;
      state.isHabitError = false;
      return state;
    });
    builder.addCase(deleteHabit.rejected, (state, action: AnyAction) => {
      state.isHabitFetching = false;
      state.isHabitError = true;
      state.habitError.push(action?.payload?.message);
      return state;
    });
  },
});

export const { clearHabitState } = habitSlice.actions;
export default habitSlice.reducer;
